<template>
  <div class="c-gen-nav__mobile-drawer" :class="{ 'is-open': isOpen }">
    <div class="c-gen-nav__mobile-header">
      <button class="c-gen-nav__mobile-close" @click="emit('close')">
        <Icon name="close" class="c-gen-nav__icon c-gen-nav__icon--large" />
      </button>
    </div>

    <!-- 一级菜单-->
    <div v-if="!activeL2Menu && !activeL3Menu" class="c-gen-nav__mobile-menus">
      <div v-for="(menu, i) in menus" :key="i" class="c-gen-nav__mobile-menu-item">
        <div
          class="c-gen-nav__mobile-menu-title"
          @click="hasCategories(menu) ? openL2(menu) : null"
        >
          <span v-if="hasCategories(menu)">{{ utils.sc.getFieldValue(menu, 'title') }}</span>
          <SiteLink v-else :link="getLinkField(menu)" v-bind="getLinkAttrs(menu)">
            {{ utils.sc.getFieldValue(menu, 'title') }}
          </SiteLink>
          <span v-if="hasCategories(menu)" class="c-gen-nav__mobile-arrow">
            <Icon name="arrow-right" class="c-gen-nav__icon" />
          </span>
        </div>
      </div>
    </div>

    <!-- 二级菜单 (Categories) -->
    <div v-if="activeL2Menu && !activeL3Menu" class="c-gen-nav__mobile-menus">
      <button class="c-gen-nav__mobile-back" @click="activeL2Menu = null">
        <Icon name="arrow-left" class="c-gen-nav__icon" /> Back
      </button>
      <div class="c-gen-nav__mobile-submenu-title">
        {{ utils.sc.getFieldValue(activeL2Menu, 'title') }}
      </div>
      <div
        v-for="cat in getCategories(activeL2Menu)"
        :key="utils.sc.getFieldValue(cat, 'title')"
        class="c-gen-nav__mobile-menu-item"
      >
        <div class="c-gen-nav__mobile-menu-title" :class="{ 'has-submenu': hasItems(cat) }">
          <SiteLink :link="getLinkField(cat)" v-bind="getLinkAttrs(cat)">
            {{ getTitle(cat) }}
          </SiteLink>
          <button
            v-if="hasItems(cat)"
            class="c-gen-nav__mobile-arrow"
            type="button"
            @click.stop="openL3(cat)"
          >
            <Icon name="arrow-right" class="c-gen-nav__icon" />
          </button>
        </div>
      </div>
    </div>

    <!-- 三级菜单 (Items + SubItems) -->
    <div v-if="activeL3Menu" class="c-gen-nav__mobile-menus">
      <button class="c-gen-nav__mobile-back" @click="activeL3Menu = null">
        <Icon name="arrow-left" class="c-gen-nav__icon" /> Back
      </button>
      <div class="c-gen-nav__mobile-submenu-title">
        {{ utils.sc.getFieldValue(activeL3Menu, 'title') }}
      </div>
      <div
        v-for="item in getItems(activeL3Menu)"
        :key="utils.sc.getFieldValue(item, 'title')"
        class="c-gen-nav__mobile-menu-item"
      >
        <div class="c-gen-nav__mobile-menu-title">
          <SiteLink
            class="c-gen-nav__mobile-link-title"
            :link="getLinkField(item)"
            v-bind="getLinkAttrs(item)"
          >
            {{ getTitle(item) }}
          </SiteLink>
          <button
            v-if="hasSubItems(item)"
            class="c-gen-nav__mobile-arrow"
            type="button"
            @click.stop="toggleL3Item(item)"
          >
            <Icon
              name="arrow-down"
              class="c-gen-nav__icon"
              :class="{
                'is-rotated': expandedL3Item === utils.sc.getFieldValue(item, 'title'),
              }"
            />
          </button>
        </div>
        <ul
          v-if="expandedL3Item === utils.sc.getFieldValue(item, 'title')"
          class="c-gen-nav__mobile-l3list"
        >
          <li v-for="sub in getSubItems(item)" :key="utils.sc.getFieldValue(sub, 'title')">
            <SiteLink
              class="c-gen-nav__mobile-l3list-title"
              :link="getLinkField(sub)"
              v-bind="getLinkAttrs(sub)"
            >
              {{ getTitle(sub) }}
            </SiteLink>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import utils from '@/utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import * as querystring from 'querystring';
import { MenuItemFields } from '@/types/components/Header';
import { ScItem } from '@/types/sitecore';
import SiteLink from '@/elements/SiteLink.vue';

const props = defineProps<{
  menus: ScItem<MenuItemFields>[];
  isOpen: boolean;
}>();

const emit = defineEmits(['close']);

const activeL2Menu = ref<any>(null);
const activeL3Menu = ref<any>(null);
const expandedL3Item = ref('');

watch(
  () => props.isOpen,
  (newVal) => {
    if (!newVal) {
      activeL2Menu.value = null;
      activeL3Menu.value = null;
      expandedL3Item.value = '';
    }
  }
);

const getCategories = (menu: any) => {
  const f = menu?.fields || menu || {};
  const c = f.categories;
  return Array.isArray(c) ? c : [];
};

const hasCategories = (menu: any) => {
  return getCategories(menu).length > 0;
};

const getItems = (cat: any) => {
  const f = cat?.fields || cat || {};
  const i = f.items;
  return Array.isArray(i) ? i : [];
};

const hasItems = (cat: any) => {
  return getItems(cat).length > 0;
};

const getSubItems = (item: any) => {
  const f = item?.fields || item || {};
  const s = f.subItems;
  return Array.isArray(s) ? s : [];
};

const hasSubItems = (item: any) => {
  return getSubItems(item).length > 0;
};

const getTitle = (item: any) => {
  const title = utils.sc.getFieldObj(item, 'title');
  return title?.value || title || item?.displayName || item?.name || '';
};

const getLinkHref = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  let url = link?.value?.href || link?.href || '';

  if (!url) return '';
  if (/^(mailto|tel):/gi.test(url)) return url;
  if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) return url.replace(/^https?:\/\//gi, '');

  const queryString = link?.value?.querystring || link?.querystring;
  if (queryString) {
    try {
      url = appendQuery(url, querystring.parse(queryString));
    } catch (error) {
      console.warn('Failed to parse querystring:', queryString, error);
    }
  }

  const anchor = link?.value?.anchor || link?.anchor;
  if (anchor) {
    url = `${url}?id=${anchor}`;
  }

  return getBetterUrl(url) ?? '';
};

const getLinkAttrs = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  const value = link?.value || link || {};
  const attrs: Record<string, string> = {};

  if (value.target) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.class) attrs.class = value.class;
  if (value.target === '_blank') attrs.rel = 'noopener noreferrer';

  return attrs;
};

const getLinkField = (item: any) => {
  const link = utils.sc.getFieldObj(item, 'link');
  return {
    value: {
      ...(link?.value || link || {}),
      href: link?.value?.href || link?.href || '',
    },
  };
};

const openL2 = (menu: any) => {
  activeL2Menu.value = menu;
  activeL3Menu.value = null;
  expandedL3Item.value = '';
};

const openL3 = (cat: any) => {
  activeL3Menu.value = cat;
};

const toggleL3Item = (item: any) => {
  const title = utils.sc.getFieldValue(item, 'title');
  if (hasSubItems(item)) {
    expandedL3Item.value = expandedL3Item.value === title ? '' : title;
  }
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-gen-nav {
  &__mobile-drawer {
    position: fixed;
    top: 0;
    right: -100%;
    width: 100%;
    height: 100vh;
    background: $white;
    z-index: 2000;
    transition: right 0.3s ease;
    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    &.is-open {
      right: 0;
    }

    @include tablet-landscape {
      display: none;
    }
  }

  &__mobile-header {
    padding: 16px;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: flex-end;
  }

  &__mobile-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #333;
    padding: 0;
  }

  &__mobile-menus {
    flex: 1;
    overflow-y: auto;
    position: relative;
  }

  &__mobile-menu-item {
    border-bottom: 1px solid #eaeaea;
  }

  &__mobile-menu-title {
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    cursor: pointer;
    color: #333;
    font-size: 15px;

    a,
    .e-site-link,
    .c-gen-nav__mobile-link-title {
      color: #333;
      text-decoration: none;
      flex: 1;
    }

    .c-gen-nav__mobile-link-title.is-link {
      cursor: pointer;
    }

    &:hover {
      background: #f5f5f5;
    }
  }

  &__mobile-arrow {
    color: #999;
    display: flex;
    align-items: center;
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;

    .is-rotated {
      transform: rotate(180deg);
    }
  }

  &__mobile-back {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 16px;
    text-align: left;
    background: #f5f5f5;
    border: none;
    border-bottom: 1px solid #eaeaea;
    cursor: pointer;
    font-weight: 500;
    color: #333;
    font-size: 14px;
  }

  &__mobile-submenu-title {
    padding: 16px;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #eaeaea;
  }

  &__mobile-l3list {
    margin: 0;
    padding: 0 16px 12px;
    list-style: none;

    li {
      .c-gen-nav__mobile-l3list-title {
        display: block;
        padding: 10px 0;
        color: #666;
        font-size: 14px;
        border-bottom: 1px solid #f0f0f0;

        &.is-link {
          cursor: pointer;
        }

        &.is-link:hover {
          color: #7ba888;
        }
      }

      &:last-child .c-gen-nav__mobile-l3list-title {
        border-bottom: none;
      }
    }
  }
}
</style>
